import type { SubtitleStreamItem } from '../components/SubtitleStream';
import { normalizeLang } from './normalizeLang';

/**
 * 把 logDetail 里写库的译文(所有语言)灌进多语言缓存,key 用 subtitleId。
 * 历史条目的译文若只放在 translateText 上、不进缓存,后续任何别的语言的译文广播
 * 到达时 onMessage 按当前语言从缓存重取会取到空,把已显示的译文清掉(只剩原文)。
 * 不覆盖缓存里已有的同语言文本(实时广播/补翻可能比库里的更新)。
 */
export function seedTranslationCache(
  cache: Map<string, Map<string, string>>,
  subtitleList: Array<{
    subtitle_id: string;
    subtitle_translation_list?: { lang: string; text: string }[];
  }>,
): void {
  subtitleList.forEach((item) => {
    const list = item.subtitle_translation_list;
    if (!item.subtitle_id || !list || list.length === 0) return;
    let m = cache.get(item.subtitle_id);
    if (!m) {
      m = new Map();
      cache.set(item.subtitle_id, m);
    }
    list.forEach((t) => {
      if (!t.text) return;
      const lang = normalizeLang(t.lang);
      if (!m!.get(lang)) m!.set(lang, t.text);
    });
  });
}

/**
 * 合并「历史字幕」与「MQTT 实时字幕」,用 subtitleId 统一对齐 + 多语言译文缓存。
 *
 * 后端契约:
 * - 原文消息 MEET_SUBTITLE_TEXT 只有 resultId(识别引擎时间戳),无 subtitleId。
 * - 译文消息(实时 pipeline / history 补翻)都带 subtitleId;desktop 把所有 desktop_langs
 *   译文实时广播给「所有」观众(不分观众当前选什么语言)。
 *
 * 关键:前端把所有广播语言的译文按 lang 缓存(translations: key → lang → text),切语言时
 * 直接从缓存取当前语言译文 —— 旧句即使切到「desktop 正在翻、但观众之前没看」的语言,也能
 * 立刻显示,不依赖后端重推/补翻(后端实时 pipeline 不重翻旧句,desktop 语言也不触发 history 补翻)。
 *
 * @param prev            当前完整列表
 * @param historicalItems logDetail 构建的历史条目(已含 subtitleId 与按当前语言取好的译文)
 * @param normalizedLang  当前查看语言(已 normalize)
 * @param translations    多语言译文缓存:key(subtitleId/resultId) → (normalizedLang → text)
 */
export default function mergeRealtimeAndHistory(
  prev: SubtitleStreamItem[],
  historicalItems: SubtitleStreamItem[],
  normalizedLang: string,
  translations: Map<string, Map<string, string>>,
): SubtitleStreamItem[] {
  const histKeys = new Set<string>();
  historicalItems.forEach((h) => {
    histKeys.add(h.resultId);
    if (h.subtitleId) histKeys.add(h.subtitleId);
  });
  const histTexts = new Set(
    historicalItems.map((h) => (h.originalText || '').trim()).filter(Boolean),
  );

  const realtimeItems = prev
    .filter((it) => {
      // 已转历史(subtitleId / resultId 命中历史,或原文文本已在历史)→ 丢弃实时副本
      if (it.subtitleId && histKeys.has(it.subtitleId)) return false;
      if (histKeys.has(it.resultId)) return false;
      const text = (it.originalText || '').trim();
      if (text && histTexts.has(text)) return false;
      return true;
    })
    .map((it) => {
      // 译文按当前语言从多语言缓存取:切语言时旧句立刻有译文;缓存无则空(显示"翻译中")
      const bySid = it.subtitleId
        ? translations.get(it.subtitleId)?.get(normalizedLang)
        : undefined;
      const t = bySid || translations.get(it.resultId)?.get(normalizedLang) || '';
      return { ...it, translateText: t };
    });

  return [...historicalItems, ...realtimeItems];
}
